import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { LabelM } from '@sorare/core/src/atoms/typography/index.tsx';
import { Alert } from '@sorare/core/src/atoms/ui/Alert';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import type { CompetitionWarning_cardShardsChest } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  padding: var(--double-unit);

  /* Use more space on mobile if there is no promo title */
  *:not(:has(&:first-child:nth-last-child(3))) > & {
    width: max-content;
    max-width: calc(90 * var(--vw));
  }
  @media ${tabletAndAbove} {
    width: auto;
    max-width: none;
  }
  &:empty {
    display: none;
  }
`;
type Props = {
  chest: CompetitionWarning_cardShardsChest;
};

export const CompetitionWarning = ({ chest }: Props) => {
  const {
    flags: { useEssenceWarning = false },
  } = useFeatureFlags();
  if (!chest || !useEssenceWarning) return null;

  const { craftableCompetitionsNotInPool } = chest;

  if (!craftableCompetitionsNotInPool.length) return null;

  return (
    <Wrapper>
      <Alert
        title={
          <FormattedMessage defaultMessage="Some competitions are not yet available" />
        }
        message={
          <>
            {craftableCompetitionsNotInPool.map(competition => (
              <LabelM key={competition.slug} color="var(--c-yellow-400)">
                <FormattedMessage
                  defaultMessage="{competition} is not yet available to craft!"
                  values={{ competition: competition.displayName }}
                />
              </LabelM>
            ))}
          </>
        }
      />
    </Wrapper>
  );
};

CompetitionWarning.fragments = {
  cardShardsChest: gql`
    fragment CompetitionWarning_cardShardsChest on CardShardsChest {
      id
      craftableCompetitionsNotInPool {
        slug
        displayName
      }
    }
  ` as TypedDocumentNode<CompetitionWarning_cardShardsChest>,
};
